// ============================================================
// GraficoDeComposicao.js — a pizza das quatro massas
//
// Gordura, músculo, osso e massa residual, em quilos e em percentual do
// peso. É a leitura que a comissão técnica faz de relance: o percentual
// de gordura sozinho não distingue o atleta que ganhou músculo do que só
// ganhou peso, e quatro fatias distinguem.
//
// ── Por que a pizza e não barras ────────────────────────────
//
// Porque aqui as partes somam o todo, e é esse o ponto: as quatro massas
// fecham com o peso corporal. Barra lado a lado compara categorias
// independentes, que é o caso dos outros dois gráficos deste módulo, e
// não é o caso deste.
//
// ── A massa muscular é subtração ────────────────────────────
//
// Ela não tem equação: é peso menos as outras três. Qualquer erro nas
// outras três aparece nela com o sinal trocado, e por isso a fatia dela
// nunca aparece sozinha nem em destaque. O rodapé do gráfico diz de onde
// vem cada uma, que é o mínimo para alguém poder desconfiar do número.
// ============================================================

import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import {
  AZUL, VERDE_LOGO, AMARELO, CINZA_ESC, TEXTO, BORDA, FUNDO_SUAVE, RAIO, TIPO, ESP,
} from "../tema";
import { Vazio } from "./Dados";

// Uma cor por compartimento, fixa. Elas não podem girar entre leituras:
// a mesma fatia tem de ser a mesma massa em toda a tela e em todo
// relatório, senão duas pizzas do mesmo atleta parecem dizer coisas
// diferentes.
export const CORES_DA_COMPOSICAO = {
  massaMuscular: AZUL,
  massaGorda: AMARELO,
  massaOssea: CINZA_ESC,
  massaResidual: VERDE_LOGO,
};

const FATIAS = [
  { id: "massaMuscular", rotulo: "Massa muscular", fonte: "peso menos as outras três" },
  { id: "massaGorda", rotulo: "Massa adiposa", fonte: "protocolo de dobras" },
  { id: "massaOssea", rotulo: "Massa óssea", fonte: "Rocha (1975)" },
  { id: "massaResidual", rotulo: "Massa residual", fonte: "Würch (1974)" },
];

function dec(v, casas = 1) {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toFixed(casas).replace(".", ",");
}

export default function GraficoDeComposicao({ fracionamento, altura = 240, isMobile = false }) {
  if (!fracionamento || !fracionamento.completo) {
    return (
      <Vazio titulo="O fracionamento aparece quando a coleta tiver o diâmetro do punho e o do fêmur." />
    );
  }

  const dados = FATIAS.map(f => ({
    ...f,
    kg: fracionamento[f.id],
    // O percentual vem do módulo e não é recalculado aqui: duas contas
    // do mesmo percentual divergem na primeira vez que uma delas mudar.
    pct: fracionamento[
      f.id === "massaGorda" ? "percentualGordura"
      : f.id === "massaOssea" ? "percentualOsseo"
      : f.id === "massaResidual" ? "percentualResidual"
      : "percentualMuscular"
    ],
  })).filter(d => Number.isFinite(d.kg) && d.kg > 0);

  return (
    <div style={{
      display: "grid", gap: ESP.lg,
      gridTemplateColumns: isMobile ? "1fr" : "minmax(200px, 260px) 1fr",
      alignItems: "center",
    }}>
      <div style={{ width: "100%", height: altura }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={dados}
              dataKey="kg"
              nameKey="rotulo"
              innerRadius="52%"
              outerRadius="82%"
              paddingAngle={2}
              stroke="none"
              isAnimationActive={false}
            >
              {dados.map(d => (
                <Cell key={d.id} fill={CORES_DA_COMPOSICAO[d.id]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: FUNDO_SUAVE, border: `1px solid ${BORDA}`,
                borderRadius: RAIO.md, fontSize: 12.5, color: TEXTO,
              }}
              formatter={(v, nome) => [`${dec(v, 2)} kg`, nome]}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* A legenda é uma tabela e não uma fileira de bolinhas: o número
          em quilos é o que a comissão usa, e o percentual é o que ela
          compara entre avaliações. Os dois têm de caber na mesma linha. */}
      <table style={{ borderCollapse: "collapse", width: "100%", ...TIPO.apoio }}>
        <tbody>
          {dados.map(d => (
            <tr key={d.id} style={{ borderTop: `1px solid ${BORDA}` }}>
              <td style={{ padding: "7px 8px 7px 0", width: 18 }}>
                <span style={{
                  display: "inline-block", width: 11, height: 11, borderRadius: 3,
                  background: CORES_DA_COMPOSICAO[d.id],
                }} />
              </td>
              <td style={{ padding: "7px 8px", color: TEXTO }}>
                {d.rotulo}
                <span style={{ color: CINZA_ESC }}> · {d.fonte}</span>
              </td>
              <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 600, color: TEXTO }}>
                {dec(d.kg, 2)} kg
              </td>
              <td style={{ padding: "7px 0", textAlign: "right", color: CINZA_ESC, width: 56 }}>
                {dec(d.pct)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
